import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { Button, Card, Divider, Grid, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import { AppRoutes } from '~/app/app.routes';
import { DashboardService } from '~/service/service.dashboard';
import { UIService } from '~/service/service.ui';
import { useService } from '~/shared/service/service.base';
import { useTr } from '~/text';
import { DashboardActivityItem } from '~/view/dashboard/dashboard.activity';
import { DashboardCard } from '~/view/dashboard/dashboard.card';
import { DashboardCountriesChart } from '~/view/dashboard/dashboard.countries';
import { DashboardHeader } from '~/view/dashboard/dashboard.header';
import { DashboardTopButton } from '~/view/dashboard/dashboard.top';
import { DashboardBrandsShelvesDialog, DashboardBrandsViewedDialog } from '~/view/dialog/dialog.dashboard.brands';
import { DashboardFragrancesDialog } from '~/view/dialog/dialog.dashboard.fragrances';
import { GridContainer } from '~/view/grid/grid.container';
import { Col, Row, Text } from '~/view/view.box';

export const DashboardPeriodsPage = observer(() => {
	const t = useTr(['dashboard']);
	const [ui, svc] = useService(UIService, DashboardService);
	ui.usePageTitle('Dashboard by Period');
	svc.usePeriodAnalytics();
	svc.useActivities();

	return (
		<GridContainer async={svc.asyncPeriod} slotsProps={{ async: { withoutLoading: false } }}>
			<Col flex={1} justifyContent="flex-start" gap={3} py={2}>
				<Grid container spacing={2} flex={1}>
					<Grid flex={1} item xs={12} md={9} xl={10} display="flex" flexDirection="column" gap={2}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<DashboardHeader />
							</Grid>
							<Grid item xs={12} maxHeight="calc(100vh - 140px)" overflow="auto">
								<Grid container spacing={2} wrap="wrap">
									<Divider />
									{svc.periodData.map(d => (
										<Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={d.key}>
											<DashboardCard
												title={d.key}
												invert={d.invert}
												currValue={d.value}
												desc={d.desc}
												percent={d.percent}
												prevValue={d.prev}
											/>
										</Grid>
									))}
								</Grid>
								<Grid container py={2} spacing={2}>
									<Grid item xs={12} lg={4} display="flex">
										<Card elevation={2} sx={{ flex: 1 }}>
											<Col gap={2} p={2} color="grey.800">
												<DashboardTopButton
													onClick={() => ui.showDialog(DashboardBrandsShelvesDialog)}
												>
													TOP 20 brands on shelves
												</DashboardTopButton>
												<DashboardBrandsShelvesDialog />

												<DashboardTopButton
													onClick={() => ui.showDialog(DashboardBrandsViewedDialog)}
												>
													TOP 20 viewed brands
												</DashboardTopButton>
												<DashboardBrandsViewedDialog />

												<DashboardTopButton
													onClick={() => ui.showDialog(DashboardFragrancesDialog)}
												>
													TOP 20 perfumes that received 5 from users
												</DashboardTopButton>
												<DashboardFragrancesDialog />
											</Col>
										</Card>
									</Grid>
									<Grid item xs={12} lg={8} pr={1} display="flex">
										<DashboardCountriesChart
											countries={svc.periodCountriesList}
											source={svc.countriesSource}
											onSourceChange={svc.setCountriesSource}
											height={400}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12} md={3} xl={2} display="flex">
						<Col
							flex={1}
							gap={1}
							pr={1}
							overflow="auto"
							maxHeight="calc(100vh - 110px)"
							justifyContent="flex-start"
						>
							<Row
								alignItems="center"
								justifyContent="space-between"
								position="sticky"
								pb={1}
								top={0}
								zIndex={1}
								bgcolor="background.paper"
							>
								<Text variant="subtitle1" fontWeight={700}>
									{t('actions')}
								</Text>
								<Link to={AppRoutes.DashboardActivities.path}>
									<Button size="small" variant="text" endIcon={<ArrowOutwardIcon />}>
										{t('actionsSeeAll')}
									</Button>
								</Link>
							</Row>
							{!!svc.periodActivitiesList.primary.length && (
								<StickyText color="text.secondary" fontWeight={700}>
									PRIMARY
								</StickyText>
							)}
							{svc.periodActivitiesList.primary.map(a => (
								<DashboardActivityItem activity={a} key={a.key} />
							))}
							{!!svc.periodActivitiesList.primary.length && <Divider sx={{ my: 1 }} />}
							{!!svc.periodActivitiesList.secondary.length && (
								<StickyText color="text.secondary" fontWeight={700}>
									SECONDARY
								</StickyText>
							)}
							{svc.periodActivitiesList.secondary.map(a => (
								<DashboardActivityItem activity={a} key={a.key} />
							))}
							{!svc.periodActivitiesList.primary.length && !svc.periodActivitiesList.secondary.length && (
								<Col flex={1} alignItems="center" justifyContent="center">
									<Text color="text.secondary">{t('noActions')}</Text>
								</Col>
							)}
						</Col>
					</Grid>
				</Grid>
			</Col>
		</GridContainer>
	);
});

const StickyText = styled(Text)(({ theme }) => ({
	position: 'sticky',
	top: 0,
	backgroundColor: theme.palette.background.paper,
	zIndex: 1,
	width: 'max-content',
}));
